import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IBehavior, IBehaviorGoalName } from '../utils/types';

const name = 'behaviors';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<BehaviorState>) => createExtraReducers(builder),
});

export interface BehaviorState {
  list: Array<IBehavior>;
  error: any;
  loading: boolean;
  current: IBehavior | null;
  goalNames: { [key: number]: Array<IBehaviorGoalName> } | null;
  goalNamesLoading: boolean;
  allGoalTemplates: Array<IBehaviorGoalName>;
  lastFetchedGoalTemplate: IBehaviorGoalName | null;
}

function createInitialState(): BehaviorState {
  return {
    list: [],
    loading: false,
    error: null,
    current: null,
    goalNames: null,
    goalNamesLoading: false,
    allGoalTemplates: [],
    lastFetchedGoalTemplate: null,
  };
}

function createReducers() {
  return { clearLastFetchedGoalTemplate };

  function clearLastFetchedGoalTemplate(state: BehaviorState) {
    state.lastFetchedGoalTemplate = null;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/behaviors`;

  return {
    getList: getList(),
    createBehavior: createBehavior(),
    getGoalNames: getGoalNames(),
    createGoalName: createGoalName(),
    getAllGoalTemplates: getAllGoalTemplates(),
    getGoalTemplate: getGoalTemplate(),
    updateGoalTemplateStatus: updateGoalTemplateStatus(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.get({ url: `${baseUrl}/`, getAccessTokenSilently });
      return response;
    });
  }

  function createBehavior() {
    return createAsyncThunk(
      `${name}/createBehavior`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function getGoalNames() {
    return createAsyncThunk(
      `${name}/getGoalNames`,
      async ({ behaviorId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${behaviorId}/goal_names/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createGoalName() {
    return createAsyncThunk(
      `${name}/createGoalName`,
      async ({ behaviorId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${behaviorId}/goal_names/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function getAllGoalTemplates() {
    return createAsyncThunk(
      `${name}/getAllGoalTemplates`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/goal_names/all/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getGoalTemplate() {
    return createAsyncThunk(
      `${name}/getGoalTemplate`,
      async ({ behaviorId, goalTemplateId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${behaviorId}/goal_names/${goalTemplateId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateGoalTemplateStatus() {
    return createAsyncThunk(
      `${name}/updateGoalTemplateStatus`,
      async ({ behaviorId, goalTemplateId, isActive, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch({
          url: `${baseUrl}/${behaviorId}/goal_names/${goalTemplateId}/${
            isActive ? 'activate' : 'deactivate'
          }/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<BehaviorState>) {
  const {
    getList,
    createBehavior,
    getGoalNames,
    createGoalName,
    getAllGoalTemplates,
    getGoalTemplate,
    updateGoalTemplateStatus,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: BehaviorState, action: PayloadAction<IBehavior[]>) => {
      state.loading = false;
      state.list = action.payload;
      state.current = state.list.length > 0 ? state.list[0] : null;
    })
    .addCase(getList.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // createBehavior()
    .addCase(createBehavior.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createBehavior.fulfilled, (state: BehaviorState, action: PayloadAction<IBehavior>) => {
      state.loading = false;
      state.current = action.payload;
    })
    .addCase(createBehavior.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getGoalNames()
    .addCase(getGoalNames.pending, (state: BehaviorState) => {
      state.goalNamesLoading = true;
      state.error = null;
    })
    .addCase(getGoalNames.fulfilled, (state: BehaviorState, action: any) => {
      state.goalNamesLoading = false;
      state.goalNames = {
        ...state.goalNames,
        [action.meta.arg.behaviorId]: action.payload,
      };
    })
    .addCase(getGoalNames.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.goalNamesLoading = false;
      toast.error(state.error.message);
    })

    // createGoalName()
    .addCase(createGoalName.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createGoalName.fulfilled, (state: BehaviorState) => {
      state.loading = false;
    })
    .addCase(createGoalName.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getAllGoalTemplates()
    .addCase(getAllGoalTemplates.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getAllGoalTemplates.fulfilled, (state: BehaviorState, action: any) => {
      state.loading = false;
      state.allGoalTemplates = action.payload;
    })
    .addCase(getAllGoalTemplates.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getGoalTemplate()
    .addCase(getGoalTemplate.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getGoalTemplate.fulfilled, (state: BehaviorState, action: any) => {
      state.loading = false;
      state.lastFetchedGoalTemplate = action.payload;
    })
    .addCase(getGoalTemplate.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateGoalTemplateStatus()
    .addCase(updateGoalTemplateStatus.pending, (state: BehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateGoalTemplateStatus.fulfilled, (state: BehaviorState) => {
      state.loading = false;
      if (state.lastFetchedGoalTemplate) {
        state.lastFetchedGoalTemplate = {
          ...state.lastFetchedGoalTemplate,
          is_active: !state.lastFetchedGoalTemplate.is_active,
        };
      }
    })
    .addCase(updateGoalTemplateStatus.rejected, (state: BehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const behaviorActions = { ...slice.actions, ...extraActions };
export const behaviorReducer = slice.reducer;
